/**
 * API client utility that handles the base URL
 * Uses VITE_API_BASE_URL env var if set, otherwise uses current origin
 */

import i18n from "@/i18n";

const getApiBase = () => {
  // For development, use relative URLs (same origin)
  // For production with separate frontend/backend, use VITE_API_BASE_URL
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  return "";
};

export const apiUrl = (path: string): string => {
  const base = getApiBase();
  if (base) {
    return `${base}${path}`;
  }
  return path;
};

/**
 * Normalize asset URL (images, files) so it is always absolute.
 * Prevents 404 when backend returns a bare filename (e.g. "1770699599584.jpg") which would otherwise be resolved relative to the current path.
 */
export function toAbsoluteAssetUrl(url: string | null | undefined): string {
  if (!url || typeof url !== "string") return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/")) return apiUrl(url);
  return apiUrl(`/uploads/${url}`);
}

/**
 * Generate unique correlation ID for request tracing
 */
function generateCorrelationId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
}

/**
 * Get or create correlation ID for current request chain
 * Stores in sessionStorage to maintain across page reloads
 */
function getCorrelationId(): string {
  const key = 'api-correlation-id';
  let correlationId = sessionStorage.getItem(key);
  
  if (!correlationId) {
    correlationId = generateCorrelationId();
    sessionStorage.setItem(key, correlationId);
  }
  
  return correlationId;
}

/** JWT from login (email auth); used for API Authorization header.
 *  Audit H-18: tokens must not live in Web Storage. Always returns null so that
 *  API calls rely on the httpOnly `auth` cookie set at login. Any legacy copy
 *  is scrubbed from storage here as well. */
function getStoredAuthToken(): string | null {
  try {
    localStorage.removeItem("email_jwt");
    sessionStorage.removeItem("email_jwt");
  } catch {
    // ignore – storage denied
  }
  return null;
}

/** Whether we have a stored auth token (email mode login). */
export function hasStoredAuthToken(): boolean {
  return !!getStoredAuthToken();
}

/** Cookie-based email auth: preferences sync uses credentials, not a visible JWT. */
export function isEmailAuthMode(): boolean {
  return import.meta.env.VITE_FEATURE_AUTH === "email";
}

/** Current app locale for API (backend can return translated messages). */
function getAppLocale(): string {
  const raw = typeof i18n.language === "string" ? i18n.language.split("-")[0] : "";
  if (raw === "sk" || raw === "en" || raw === "hu") return raw;
  return "hu";
}

/**
 * Enhanced fetch wrapper that adds correlation ID and standard headers
 * Supports AbortController for request cancellation
 */
export const apiFetch = async (
  path: string,
  init?: RequestInit & { signal?: AbortSignal },
): Promise<Response> => {
  const url = apiUrl(path);
  const correlationId = getCorrelationId();
  
  // Merge headers, ensuring correlation ID is always present
  const headers = new Headers(init?.headers);
  headers.set('X-Request-ID', correlationId);
  const locale = getAppLocale();
  headers.set('Accept-Language', locale);
  headers.set('X-App-Locale', locale);
  const token = getStoredAuthToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (!headers.has('Content-Type') && (init?.method === 'POST' || init?.method === 'PUT' || init?.method === 'PATCH')) {
    headers.set('Content-Type', 'application/json');
  }
  
  // Create AbortController if signal is not provided but we want cancellation support
  let abortController: AbortController | null = null;
  let signal = init?.signal;
  
  // If no signal provided, create a default one for timeout (optional, can be disabled)
  // For now, we use the signal if provided, otherwise use undefined (no timeout)
  // The caller can provide their own AbortController if needed
  
  const response = await fetch(url, {
    ...init,
    headers,
    credentials: init?.credentials ?? "include",
    signal: signal || init?.signal,
  });

  // If 403 with not_approved, notify app so we can show "pending approval" message
  if (response.status === 403) {
    response
      .clone()
      .json()
      .then((data: { error?: string }) => {
        if (data?.error === "not_approved") {
          window.dispatchEvent(new CustomEvent("auth:not_approved"));
        }
      })
      .catch(() => {});
  }

  // Log correlation ID from response if available (for debugging)
  const responseCorrelationId = response.headers.get('X-Request-ID');
  if (responseCorrelationId && import.meta.env.DEV) {
    console.debug(`[API] Request ${correlationId} → Response ${responseCorrelationId}`);
  }

  // Cleanup abort controller if we created one
  if (abortController) {
    abortController = null;
  }

  return response;
};
